import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilterX, Layers, UserMinus, UserPlus, Users } from "lucide-react";
import {
  AppButton, AppDataTable, Callout, EmptyState, FilterToolbar, IconButton, SegmentedControl, StarRating,
  StatusBadge, UserAvatar,
} from "../../ui";
import AddToBatchModal from "../../Students/AddToBatch/AddToBatchModal";
import apiClient from "../../../utils/apiClient";
import { formatCurrency, formatDate, DASH } from "../../../utils/format";
import { reviewSummary, studentFeeInBatch } from "./batchDetails";
import AddStudentsModal from "./AddStudentsModal";
import RemoveStudentsModal from "./RemoveStudentsModal";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_VIEWS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All" },
];

const FeeStatus = ({ fee }) => {
  if (fee.state === "unset") return <StatusBadge status="neutral">Not set</StatusBadge>;
  if (fee.state === "pending") return <StatusBadge status="warning">{formatCurrency(fee.pending)} pending</StatusBadge>;
  return <StatusBadge status="paid" domain="feeBalance" />;
};

const ReviewCell = ({ review }) => {
  if (!review) return <span className="tw-text-content-subtle">{DASH}</span>;
  const detail = `Performance ${review.performance} of 5, participation ${review.participation} of 5`
    + `${review.by ? `, by ${review.by}` : ""}${review.remark ? `. “${review.remark}”` : ""}`;
  return (
    <span className="tw-flex tw-flex-col tw-gap-0.5" title={detail}>
      <StarRating value={review.average} readOnly size="sm" label="Average review" />
      {review.by && <span className="tw-text-xs tw-text-content-subtle">by {review.by}</span>}
      <span className="tw-sr-only">{detail}</span>
    </span>
  );
};

/**
 * BatchStudentsTab — the batch's roster: who is in it, how to reach them, what
 * they owe here, how their instructor rates them; and adding, moving or
 * removing people.
 *
 * Server-paginated (`/students/list?batch=`), so every control maps to a query
 * parameter: the Active · Inactive · All view is `status`, and the funnel on
 * the student column is `search` — which matches the NAME only. There is no
 * sort parameter, so no column offers a sort arrow.
 *
 * Props:
 *   batchId
 *   showFees  — the plan and the caller have fee access
 *   canEnrol  — may add, and move people to another batch (`manage_enrollments`)
 *   canRemove — may take people out of this batch: `canEnrol`, while the batch
 *               has not ended (an ended batch keeps its roster as a record)
 *   batch     — { _id, name, courseName }, for the removal dialog's words
 *   terms     — useAudienceTerm()
 *   onNotify(message, type)
 *   onRosterChange() — after anyone joins or leaves, so the page refreshes its counts
 */
const BatchStudentsTab = ({ batchId, showFees, canEnrol, canRemove, batch, terms, onNotify, onRosterChange }) => {
  const navigate = useNavigate();
  const [view, setView] = useState("active");
  const [columnFilters, setColumnFilters] = useState({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [selected, setSelected] = useState([]);
  const [nonce, setNonce] = useState(0);
  const [roster, setRoster] = useState({ rows: [], total: 0, totalPages: 0, loading: true, error: null });
  const [reviews, setReviews] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  // The students the removal dialog is asking about, fixed while it is open.
  const [removing, setRemoving] = useState(null);
  // Names of every roster row seen, so a selection that spans pages can still
  // be named in the removal dialog.
  const seen = useRef(new Map());

  const nameFilter = columnFilters.student || "";
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(nameFilter.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [nameFilter]);

  useEffect(() => {
    let cancelled = false;
    setRoster((r) => ({ ...r, loading: true, error: null }));
    apiClient.get("/students/list", {
      params: { batch: batchId, page, pageSize, ...(search && { search }), ...(view && { status: view }) },
    }).then(({ data }) => {
      if (cancelled) return;
      const total = data.total || 0;
      (data.students || []).forEach((s) => seen.current.set(String(s._id), { _id: String(s._id), name: s.name || "" }));
      setRoster({
        rows: data.students || [],
        total,
        totalPages: data.totalPages || Math.ceil(total / pageSize),
        loading: false,
        error: null,
      });
    }).catch((error) => {
      if (!cancelled) setRoster({ rows: [], total: 0, totalPages: 0, loading: false, error });
    });
    return () => { cancelled = true; };
  }, [batchId, page, pageSize, search, view, nonce]);

  useEffect(() => {
    let cancelled = false;
    apiClient.get(`/student-reviews/batch/${batchId}`)
      .then(({ data }) => {
        if (!cancelled) setReviews(Object.fromEntries((data.reviews || []).map((r) => [String(r.studentId), r])));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [batchId]);

  const rosterIds = useMemo(() => new Set(roster.rows.map((s) => String(s._id))), [roster.rows]);

  // Removing the last rows of the last page leaves it empty: step back to the
  // page that now ends the roster.
  useEffect(() => {
    if (!roster.loading && !roster.error && roster.totalPages > 0 && page > roster.totalPages) setPage(roster.totalPages);
  }, [roster.loading, roster.error, roster.totalPages, page]);

  const reload = () => setNonce((n) => n + 1);
  const removeSelected = () => setRemoving(selected.map((id) => seen.current.get(String(id)) || { _id: String(id), name: "" }));
  const afterRemoval = ({ removedIds }, message) => {
    const left = new Set(removedIds);
    setSelected((prev) => prev.filter((id) => !left.has(String(id))));
    reload();
    onRosterChange();
    if (message) onNotify(message);
  };
  const changeView = (next) => { setView(next); setPage(1); };
  const filtersOn = !!nameFilter;

  const columns = [
    {
      key: "student",
      label: terms.singular,
      filter: { type: "text", placeholder: "Name contains…" },
      render: (s) => (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
          <UserAvatar name={s.name} size="sm" />
          <span className="tw-min-w-0">
            <span className="tw-block tw-truncate tw-font-medium tw-text-content">{s.name}</span>
            <span className="tw-block tw-truncate tw-text-xs tw-text-content-subtle">{s.course?.name || DASH}</span>
          </span>
        </span>
      ),
    },
    {
      key: "contact",
      label: "Contact",
      render: (s) => (
        <span className="tw-flex tw-flex-col tw-text-xs tw-text-content-muted">
          <span>{s.mobile || DASH}</span>
          {s.email && <span className="tw-max-w-[200px] tw-truncate">{s.email}</span>}
        </span>
      ),
    },
    showFees && {
      key: "fee",
      label: "Fee status",
      render: (s) => <FeeStatus fee={studentFeeInBatch(s, batchId)} />,
    },
    {
      key: "review",
      label: "Instructor review",
      render: (s) => <ReviewCell review={reviewSummary(reviews[String(s._id)])} />,
    },
    {
      key: "joined",
      label: "Joined",
      render: (s) => <span className="tw-whitespace-nowrap tw-text-content-muted">{formatDate(s.joinedDate)}</span>,
    },
    canRemove && {
      key: "actions",
      label: "",
      width: 56,
      pinned: "right",
      resizable: false,
      // The row opens the student, so the button must stop the click — and the
      // Enter / Space the row also listens for — from reaching it.
      render: (s) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <IconButton
            size="sm"
            variant="ghost-destructive"
            icon={UserMinus}
            label={`Remove ${s.name} from this batch`}
            onClick={() => setRemoving([{ _id: String(s._id), name: s.name || "" }])}
          />
        </div>
      ),
    },
  ].filter(Boolean);

  const countLabel = `${roster.total} ${roster.total === 1 ? terms.lower : terms.lowerPlural}`;

  const toolbar = (
    <FilterToolbar
      flush
      actions={canEnrol && (
        <>
          {selected.length > 0 && (
            <AppButton size="sm" variant="outline" onClick={() => setMoveOpen(true)}>
              <Layers size={16} /> Add to another batch ({selected.length})
            </AppButton>
          )}
          {canRemove && selected.length > 0 && (
            <AppButton size="sm" variant="outline-destructive" onClick={removeSelected}>
              <UserMinus size={16} /> Remove from batch ({selected.length})
            </AppButton>
          )}
          <AppButton size="sm" onClick={() => setAddOpen(true)}>
            <UserPlus size={16} /> Add {terms.lowerPlural}
          </AppButton>
        </>
      )}
    >
      <SegmentedControl label={`Show ${terms.lowerPlural}`} value={view} onChange={changeView} options={STATUS_VIEWS} />
      <span className="tw-text-[0.8125rem] tw-text-content-subtle" aria-live="polite">
        {roster.loading ? "Loading…" : countLabel}
      </span>
      {filtersOn && (
        <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>
          <FilterX size={16} /> Clear filters
        </AppButton>
      )}
    </FilterToolbar>
  );

  return (
    <>
      {roster.error && (
        <Callout
          tone="danger"
          role="alert"
          className="tw-mb-3"
          action={<AppButton size="xs" variant="outline" onClick={reload}>Try again</AppButton>}
        >
          {roster.error?.response?.data?.message || `Could not load this batch's ${terms.lowerPlural}.`}
        </Callout>
      )}

      <AppDataTable
        fill={false}
        toolbar={toolbar}
        columns={columns}
        rows={roster.rows}
        rowKey={(s) => String(s._id)}
        onRowClick={(s) => navigate(`/students/${s._id}`)}
        selectable={canEnrol}
        selectedRows={selected}
        onSelectionChange={setSelected}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) => setColumnFilters((prev) => ({ ...prev, [key]: value }))}
        loading={roster.loading}
        emptyState={(
          <EmptyState
            icon={Users}
            title={filtersOn ? `No ${terms.lowerPlural} match that name` : `No ${view ? `${view} ` : ""}${terms.lowerPlural} in this batch`}
            action={filtersOn
              ? <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>Clear filters</AppButton>
              : undefined}
          />
        )}
        pagination={{
          page,
          totalPages: roster.totalPages,
          total: roster.total,
          pageSize,
          onChange: setPage,
          infoLabel: terms.lowerPlural,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
        }}
      />

      {canEnrol && (
        <>
          <AddStudentsModal
            show={addOpen}
            onHide={() => setAddOpen(false)}
            batchId={batchId}
            rosterIds={rosterIds}
            terms={terms}
            onAdded={(count, message) => {
              reload();
              onRosterChange();
              if (message) onNotify(message);
            }}
          />
          {canRemove && (
            <RemoveStudentsModal
              show={!!removing}
              onHide={() => setRemoving(null)}
              batch={batch}
              students={removing || []}
              terms={terms}
              onRemoved={afterRemoval}
            />
          )}
          <AddToBatchModal
            show={moveOpen}
            onHide={() => setMoveOpen(false)}
            studentIds={selected}
            contextBatchId={batchId}
            showFees={showFees}
            onSaved={({ message, closed }) => {
              setSelected([]);
              reload();
              onRosterChange();
              if (closed) onNotify(message);
            }}
          />
        </>
      )}
    </>
  );
};

export default BatchStudentsTab;
